import { FormlyFieldConfig } from '@ngx-formly/core';

/**
 * Types whose character counter is delivered through
 * `FormlyWrapperMaxLengthCounter` (i.e. types that render via
 * ngx-formly/material's generic `formly-wrapper-mat-form-field`, which is the
 * only thing that consumes `props.hintEnd`). Custom field types with their
 * own inline `<mat-form-field>` template (email, bsn, kvk, custom-number,
 * custom-currency, ...) never read `props.hintEnd` — same limitation
 * documented in `clear-button.extension.ts`.
 */
const COUNTABLE_TYPES = new Set(['input', 'textarea']);

/**
 * Adds a character counter (`props.hintEnd`) to `input`/`textarea` fields
 * that have `props.maxLength` configured. Purely derived from
 * `props.maxLength` — no admin opt-in needed, unlike the 'Wisknop' addon: a
 * character limit implies a counter.
 */
export function maxLengthCounterExtension(field: FormlyFieldConfig): void {
  if (!field.props || !field.type || typeof field.type !== 'string') {
    return;
  }

  // Container fields (fieldGroup) and fields without a bound control (no
  // key) don't get a counter.
  if (field.fieldGroup || !field.key) {
    return;
  }

  if (!COUNTABLE_TYPES.has(field.type)) {
    return;
  }

  // props.maxLength is typed as string upstream (modules.model.ts) and only
  // cast to a number by form-generate.util.mjs — don't trust it to already
  // be numeric here.
  const maxLength = Number(field.props.maxLength);

  if (!maxLength || maxLength <= 0) {
    return;
  }

  // A field that already carries its own hintEnd keeps it — the counter must
  // never silently replace admin-configured hint content.
  if (field.props.hintEnd) {
    return;
  }

  const wrappers = [...(field.wrappers || [])];

  if (!wrappers.includes('max-length-counter')) {
    // Render the counter inside the Material wrapper's hintEnd slot.
    const materialWrapperIndex = wrappers.indexOf('form-field');
    wrappers.splice(materialWrapperIndex === -1 ? wrappers.length : materialWrapperIndex, 0, 'max-length-counter');
  }

  if (!wrappers.includes('form-field')) {
    wrappers.push('form-field');
  }

  field.wrappers = wrappers;
}
